import { CSSResultGroup, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { style } from './compare-page.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import 'components/discovery/product/image/image.js'
import 'components/discovery/product/price/price.js'
import 'components/ordering/order-form/components/order-form.js'
import { CscCompareClearEvent, CscCompareRemoveEvent } from 'components/discovery/compare/compare-events.js'
import {
  clearAllCompareSelections,
  removeProductIdFromAllCompareSelections,
} from 'components/discovery/compare/compare-selection-storage.js'
import { AttributeGroup, CompareBundledValue, ComparePageProduct, ComparePageProductTile } from './compare-page-types.js'


const DEFAULT_LOCALE = 'nl_NL'
const COMPARE_API_PATH = '/api/v1/compare/'
const MAX_COMPARE_PRODUCTS = 4
const COMPARE_ANIM_MS = 300

function stripGroupNamePrefix(groupName: string): string {
  const stripped = groupName.replace(/^(\d+|None)-/, '')
  return stripped === 'None' ? 'General' : stripped
}

function formatBundledValues(values: CompareBundledValue[] | undefined): string {
  if (!Array.isArray(values)) return '-'
  const parts = values
    .map((item) => (item?.value == null ? '' : String(item.value).trim()))
    .filter((part) => part !== '')
  return parts.length > 0 ? parts.join(', ') : '-'
}

/** Parses a compare-tile price string (already normalized by normalizeComparePrices) into a number for csc-price. */
function parsePrice(value: string | null): number {
  if (!value) return 0
  const parsed = Number.parseFloat(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

@customElement('csc-compare-page')
export class CSCComparePage extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Array })
  products: ComparePageProduct[] = []

  @property({
    type: Array,
    attribute: 'product-ids',
    hasChanged: (newVal: unknown, oldVal: unknown) =>
      JSON.stringify(newVal ?? []) !== JSON.stringify(oldVal ?? []),
    converter: {
      fromAttribute: (value: string | null) =>
        value ? value.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n)) : [],
      toAttribute: (value: number[]) => value.join(',')
    },
  })
  productIds: number[] = []

  /**
   * Property names to hide from the specification tables (comma-separated attribute).
   * By default all properties from the API are shown. Matching is case-insensitive.
   */
  @property({
    type: Array,
    attribute: 'hidden-properties',
    converter: {
      fromAttribute: (value: string | null) =>
        value ? value.split(',').map((s) => s.trim()).filter((s) => s !== '') : [],
      toAttribute: (value: string[]) => value.join(','),
    },
  })
  hiddenProperties: string[] = []

  @property({ type: String, attribute: 'api-base-url' })
  apiBaseUrl = ''

  @property({ type: String, attribute: 'api-full-url' })
  apiFullUrl = ''

  @property({ type: String, attribute: 'locale' })
  locale = DEFAULT_LOCALE

  @property({ type: Boolean, attribute: 'display-prices' })
  displayPrices = false

  /**
   * Minimum distance from the viewport top for the fixed preview strip (px).
   * Combined with `sticky-header-selector` height via `Math.max` (legacy templates measured `.header`).
   */
  @property({ type: Number, attribute: 'sticky-top-offset' })
  stickyTopOffset = 0

  /**
   * Theme header element used to derive top offset when `sticky-top-offset` is 0 or smaller than the header.
   * Set to empty string to disable measurement.
   */
  @property({ type: String, attribute: 'sticky-header-selector' })
  stickyHeaderSelector = '.header'

  @state()
  private isLoading = false

  @state()
  private fetchError: string | null = null

  @state()
  private onlyShowDifferences = false

  @state()
  private compareStickyStripVisible = false

  @state()
  private stickyStripDismissing = false

  /** False until layout + header offset are ready; prevents flash over site header on first paint. */
  @state()
  private stickyStripArmed = false

  @state()
  private removingProductIds = new Set<number>()

  /**
   * Strip only opens after the tile row has been visible once (avoids false show before layout
   * or when the row has not been scrolled past yet).
   */
  private productsRowWasInView = false

  @state()
  private tileProducts: Array<{ id: number; product: ComparePageProductTile }> = []

  private fetchAbort: AbortController | null = null

  /** Ensures URL `?id=` is merged into `productIds` once before paint (firstUpdated was too late for loading UI). */
  private urlCompareIdsSynced = false

  private stickyStripLayoutAttached = false

  /** Scroll containers that actually move this host (nested layouts); window alone misses these. */
  private stickyStripScrollParents: HTMLElement[] = []

  private stickyStripResizeObserver: ResizeObserver | null = null

  private stickyStripVisualViewportCleanup: (() => void) | null = null

  private scrollSyncRegion: HTMLElement | null = null

  private stickyStripHideTimer: ReturnType<typeof setTimeout> | null = null

  private clearStickyStripHideTimer(): void {
    if (this.stickyStripHideTimer != null) {
      clearTimeout(this.stickyStripHideTimer)
      this.stickyStripHideTimer = null
    }
  }

  private setStickyStripVisible(next: boolean): void {
    if (next) {
      this.clearStickyStripHideTimer()
      this.stickyStripDismissing = false
      this.compareStickyStripVisible = true
      return
    }
    if (!this.compareStickyStripVisible || this.stickyStripDismissing) return
    this.compareStickyStripVisible = false
    this.stickyStripDismissing = true
    this.clearStickyStripHideTimer()
    this.stickyStripHideTimer = setTimeout(() => {
      this.stickyStripDismissing = false
      this.stickyStripHideTimer = null
    }, COMPARE_ANIM_MS)
  }

  /** Nearest ancestor with scrollable overflow (innermost first in collectScrollParents). */
  private collectScrollParents(): HTMLElement[] {
    const out: HTMLElement[] = []
    for (let el = this.parentElement; el; el = el.parentElement) {
      if (!(el instanceof HTMLElement)) continue
      const s = getComputedStyle(el)
      if (/(auto|scroll|overlay)/.test(s.overflowY) || /(auto|scroll|overlay)/.test(s.overflowX)) {
        out.push(el)
      }
    }
    return out
  }

  private getNearestScrollRoot(): Element | null {
    const chain = this.collectScrollParents()
    return chain[0] ?? null
  }

  private measureStickyHeaderOffset(): number {
    if (typeof document === 'undefined') return 0
    const selector = this.stickyHeaderSelector.trim()
    if (!selector) return 0
    const el = document.querySelector(selector)
    return el instanceof HTMLElement ? el.offsetHeight : 0
  }

  private effectiveStickyTopPx(): number {
    return Math.max(0, this.stickyTopOffset, this.measureStickyHeaderOffset())
  }

  private applyStickyTopOffset(): void {
    this.style.setProperty('--csc-compare-page-sticky-top-offset', `${this.effectiveStickyTopPx()}px`)
  }

  /** Layout (fixed strip left/width/top) + visibility; IO + scroll listeners catch theme scroll roots. */
  private readonly onStickyStripFrame = (): void => {
    this.applyStickyTopOffset()
    this.syncStickyStripLayout()
    this.updateStickyStripVisibilityFromProductsRow()
  }

  private getStickyProductsRow(): HTMLElement | null {
    const row = this.shadowRoot?.querySelector('[data-cs-compare-products-row]')
    return row instanceof HTMLElement ? row : null
  }

  private getCompareTableEndSentinel(): HTMLElement | null {
    const el = this.shadowRoot?.querySelector('[data-cs-compare-table-end]')
    return el instanceof HTMLElement ? el : null
  }

  private getCompareTableLastRow(): HTMLElement | null {
    const rows = this.shadowRoot?.querySelectorAll('.row')
    if (!rows?.length) return null
    const last = rows[rows.length - 1]
    return last instanceof HTMLElement ? last : null
  }

  /** Table body still extends below the viewport (user has not scrolled past the end). */
  private isInCompareTableRegion(): boolean {
    const sentinel = this.getCompareTableEndSentinel()
    if (!sentinel) return true
    const top = sentinel.getBoundingClientRect().top
    const viewportBottom = typeof window !== 'undefined' ? window.innerHeight : 0
    return top > viewportBottom
  }

  /** Last attribute row is at least partially visible below the header clip line. */
  private isCompareTableBottomRowVisible(): boolean {
    const lastRow = this.getCompareTableLastRow()
    if (!lastRow) return false
    const rect = lastRow.getBoundingClientRect()
    if (rect.height < 1) return false
    const clipLine = this.stickyClipLinePx()
    const viewportBottom = typeof window !== 'undefined' ? window.innerHeight : 0
    return rect.bottom > clipLine && rect.top < viewportBottom
  }

  private stickyClipLinePx(): number {
    const clipTop = this.effectiveStickyTopPx()
    const scrollRoot = this.getNearestScrollRoot()
    const rootTop = scrollRoot instanceof HTMLElement ? scrollRoot.getBoundingClientRect().top : 0
    return Math.max(clipTop, rootTop)
  }

  /** Show while in the table body, or when the last table row is visible again after scrolling past. */
  private shouldShowStickyStrip(): boolean {
    if (!this.stickyStripArmed || this.products.length === 0 || !this.productsRowWasInView) {
      return false
    }

    const row = this.getStickyProductsRow()
    if (!row) return false
    const rowRect = row.getBoundingClientRect()
    if (rowRect.height < 1) return false

    const clipLine = this.stickyClipLinePx()

    if (rowRect.top >= clipLine) {
      return false
    }

    return this.isInCompareTableRegion() || this.isCompareTableBottomRowVisible()
  }

  private trackProductsRowInView(): void {
    const row = this.getStickyProductsRow()
    if (!row) return
    const rowRect = row.getBoundingClientRect()
    if (rowRect.height < 1) return
    const clipLine = this.stickyClipLinePx()
    const viewportBottom = typeof window !== 'undefined' ? window.innerHeight : 0
    if (rowRect.bottom > clipLine && rowRect.top < viewportBottom) {
      this.productsRowWasInView = true
    }
  }

  private updateStickyStripVisibilityFromProductsRow(): void {
    this.trackProductsRowInView()
    this.setStickyStripVisible(this.shouldShowStickyStrip())
  }

  private readonly syncStickyStripLayout = (): void => {
    if (typeof window === 'undefined') return
    if (this.products.length === 0) return
    const region = this.shadowRoot?.querySelector('.scroll-region')
    const r =
      region instanceof HTMLElement ? region.getBoundingClientRect() : this.getBoundingClientRect()
    this.style.setProperty('--csc-compare-page-sticky-content-margin-left', `${Math.round(r.left)}px`)
    this.style.setProperty('--csc-compare-page-sticky-content-width', `${Math.round(r.width)}px`)
  }

  private refreshStickyStripScrollParents(): void {
    const next = this.collectScrollParents()
    if (
      next.length === this.stickyStripScrollParents.length &&
      next.every((el, i) => el === this.stickyStripScrollParents[i])
    ) {
      return
    }
    for (const p of this.stickyStripScrollParents) {
      p.removeEventListener('scroll', this.onStickyStripFrame)
    }
    this.stickyStripScrollParents = next
    for (const p of this.stickyStripScrollParents) {
      p.addEventListener('scroll', this.onStickyStripFrame, { passive: true })
    }
  }

  private attachStickyStripLayout(): void {
    if (typeof window === 'undefined' || this.stickyStripLayoutAttached) return
    this.stickyStripLayoutAttached = true
    window.addEventListener('scroll', this.onStickyStripFrame, { passive: true })
    window.addEventListener('resize', this.onStickyStripFrame, { passive: true })
    this.stickyStripScrollParents = this.collectScrollParents()
    for (const p of this.stickyStripScrollParents) {
      p.addEventListener('scroll', this.onStickyStripFrame, { passive: true })
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.stickyStripResizeObserver = new ResizeObserver(() => {
        this.refreshStickyStripScrollParents()
        this.onStickyStripFrame()
      })
      this.stickyStripResizeObserver.observe(this)
    }
    this.compareStickyStripVisible = false
    this.onStickyStripFrame()
    requestAnimationFrame(() => {
      this.refreshStickyStripScrollParents()
      this.applyStickyTopOffset()
      this.syncStickyStripLayout()
      this.trackProductsRowInView()
      this.compareStickyStripVisible = false
      requestAnimationFrame(() => {
        this.applyStickyTopOffset()
        this.syncStickyStripLayout()
        this.trackProductsRowInView()
        this.compareStickyStripVisible = false
        this.stickyStripArmed = true
        this.updateStickyStripVisibilityFromProductsRow()
      })
    })
    if (typeof window !== 'undefined' && window.visualViewport) {
      const vv = window.visualViewport
      vv.addEventListener('scroll', this.onStickyStripFrame)
      vv.addEventListener('resize', this.onStickyStripFrame)
      this.stickyStripVisualViewportCleanup = () => {
        vv.removeEventListener('scroll', this.onStickyStripFrame)
        vv.removeEventListener('resize', this.onStickyStripFrame)
      }
    }
  }

  private detachStickyStripLayout(): void {
    if (!this.stickyStripLayoutAttached) return
    this.stickyStripLayoutAttached = false
    this.clearStickyStripHideTimer()
    this.stickyStripDismissing = false
    this.stickyStripVisualViewportCleanup?.()
    this.stickyStripVisualViewportCleanup = null
    window.removeEventListener('scroll', this.onStickyStripFrame)
    window.removeEventListener('resize', this.onStickyStripFrame)
    for (const p of this.stickyStripScrollParents) {
      p.removeEventListener('scroll', this.onStickyStripFrame)
    }
    this.stickyStripScrollParents = []
    this.stickyStripResizeObserver?.disconnect()
    this.stickyStripResizeObserver = null
    this.style.removeProperty('--csc-compare-page-sticky-content-margin-left')
    this.style.removeProperty('--csc-compare-page-sticky-content-width')
  }

  private readonly syncStickyScroll = () => {
    const inner = this.shadowRoot?.querySelector('.sticky-inner')
    if (inner instanceof HTMLElement && this.scrollSyncRegion) {
      inner.scrollLeft = this.scrollSyncRegion.scrollLeft
    }
    this.onStickyStripFrame()
  }

  private setupScrollSync(): void {
    this.teardownScrollSync()
    const region = this.shadowRoot?.querySelector('.scroll-region')
    if (!(region instanceof HTMLElement)) return
    this.scrollSyncRegion = region
    region.addEventListener('scroll', this.syncStickyScroll, { passive: true })
  }

  private teardownScrollSync(): void {
    this.scrollSyncRegion?.removeEventListener('scroll', this.syncStickyScroll)
    this.scrollSyncRegion = null
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.clearStickyStripHideTimer()
    this.urlCompareIdsSynced = false
    this.teardownCompareStickyObserver()
    this.teardownScrollSync()
    this.fetchAbort?.abort()
    this.fetchAbort = null
  }

  override willUpdate(_changedProperties: PropertyValues): void {
    super.willUpdate(_changedProperties)
    if (this.urlCompareIdsSynced || typeof window === 'undefined') return
    if (this.productIds.length > 0) {
      this.urlCompareIdsSynced = true
      return
    }
    const params = new URLSearchParams(window.location.search)
    const ids = params.getAll('id').map(Number).filter((n) => !Number.isNaN(n))
    this.urlCompareIdsSynced = true
    if (ids.length > 0) this.productIds = ids
  }

  override firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties)
    if (this.products.length > 0) {
      requestAnimationFrame(() => {
        this.setupCompareStickyObserver()
        this.setupScrollSync()
      })
    }
  }

  private syncCompareEmptyAttribute(): void {
    const showLoadingUi =
      !this.fetchError &&
      (this.isLoading || (this.productIds.length > 0 && this.products.length === 0))
    const isEmpty =
      !showLoadingUi &&
      !this.fetchError &&
      this.products.length === 0 &&
      this.productIds.length === 0
    this.toggleAttribute('compare-empty', isEmpty)
  }

  override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)
    this.syncCompareEmptyAttribute()

    if (
      this.products.length > 0 &&
      (changedProperties.has('products') ||
        changedProperties.has('stickyTopOffset') ||
        changedProperties.has('stickyHeaderSelector'))
    ) {
      this.applyStickyTopOffset()
    }

    if (
      (changedProperties.has('stickyTopOffset') || changedProperties.has('stickyHeaderSelector')) &&
      this.products.length > 0
    ) {
      requestAnimationFrame(() => this.onStickyStripFrame())
    }

    if (changedProperties.has('products')) {
      this.tileProducts = this.products.map((p) => ({
        id: Number(p.product.id),
        // Keep product in API/raw tile shape (normalized prices, non-null brand) for direct rendering.
        product: {
          ...p.product,
          prices: this.normalizeComparePrices((p.product as unknown as Record<string, unknown>)['prices']),
          brand: p.product.brand ?? { name: '' },
        },
      }))

      if (this.products.length > 0) {
        const prev = changedProperties.get('products') as ComparePageProduct[] | undefined
        if (!prev?.length) {
          this.productsRowWasInView = false
          this.compareStickyStripVisible = false
          this.stickyStripArmed = false
        }
        requestAnimationFrame(() => {
          this.setupCompareStickyObserver()
          this.setupScrollSync()
        })
      } else {
        this.teardownCompareStickyObserver()
        this.teardownScrollSync()
        this.compareStickyStripVisible = false
        this.stickyStripArmed = false
        this.productsRowWasInView = false
      }
    }

    const fetchRelevant =
      changedProperties.has('productIds') ||
      changedProperties.has('apiBaseUrl') ||
      changedProperties.has('apiFullUrl') ||
      changedProperties.has('locale')
    if (!fetchRelevant) return

    const loadedIds = this.products.map(p => Number(p.product.id))
    const alreadyLoaded =
      this.productIds.length === loadedIds.length &&
      this.productIds.every((id, i) => Number(id) === loadedIds[i])
    if (alreadyLoaded) return

    if (this.productIds.length === 0) {
      this.fetchAbort?.abort()
      this.fetchAbort = null
      this.isLoading = false

      const prevIds = changedProperties.get('productIds') as number[] | undefined
      if (prevIds !== undefined && prevIds.length > 0) {
        this.products = []
        this.fetchError = null
      }
      return
    }

    this.fetchCompareData()
  }

  private teardownCompareStickyObserver(): void {
    this.detachStickyStripLayout()
  }

  private setupCompareStickyObserver(): void {
    if (this.products.length === 0) return
    const sentinel = this.shadowRoot?.querySelector('.sticky-sentinel')
    if (!(sentinel instanceof HTMLElement)) return

    this.teardownCompareStickyObserver()
    this.attachStickyStripLayout()
  }

  private previewImageUrl(product: ComparePageProductTile): string {
    const pic = product.picture
    const def = product.default_picture
    const fromPic = pic?.normal || pic?.small || pic?.mini || ''
    const fromDef = def?.normal || def?.small || def?.mini || ''
    return fromPic || fromDef || ''
  }

  private formatComparePriceForPreview(prices: ComparePageProductTile['prices']): string {
    if (!prices || typeof prices !== 'object') return ''
    const p = prices as unknown as Record<string, unknown>
    const value =
      p['sale'] ??
      p['sale_price_no_format'] ??
      p['sale_price'] ??
      p['consumer'] ??
      p['list'] ??
      p['list_price_from_object'] ??
      p['list_price']
    if (value == null || String(value).trim() === '') return ''
    const num = Number(value)
    if (!Number.isNaN(num)) {
      const currencyCode = String(p['currency_code'] ?? 'EUR')
      try {
        return new Intl.NumberFormat(this.locale.replace('_', '-'), {
          style: 'currency',
          currency: currencyCode,
        }).format(num)
      } catch {
        return String(num)
      }
    }
    return this.normalizePriceString(value)
  }

  private normalizePriceString(value: unknown): string {
    if (value == null) return ''
    if (typeof value === 'number') return String(value)
    const raw = String(value).trim()
    if (!raw) return ''
    const cleaned = raw.replace(/[^\d,.-]/g, '')
    if (!cleaned) return ''
    const normalized =
      cleaned.includes(',') && !cleaned.includes('.')
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,/g, '')
    const num = Number(normalized)
    return Number.isNaN(num) ? cleaned : String(num)
  }

  private normalizeComparePrices(prices: unknown): ComparePageProductTile['prices'] {
    const p = (prices ?? {}) as Record<string, unknown>
    const pickFirstNonEmpty = (...values: unknown[]) => {
      for (const value of values) {
        if (value == null) continue
        if (typeof value === 'string' && value.trim() === '') continue
        return value
      }
      return ''
    }

    const saleRaw = pickFirstNonEmpty(
      p['sale'],
      p['sale_price_no_format'],
      p['sale_price'],
    )
    const saleTaxedRaw = pickFirstNonEmpty(
      p['sale_taxed'],
      p['sale_price_no_format'],
      p['sale_price'],
      saleRaw,
    )
    const listRaw = pickFirstNonEmpty(
      p['list'],
      p['list_price_from_object'],
      p['list_price'],
      saleRaw,
    )

    return {
      type: String(p['type'] ?? 'list'),
      currency_code: String(p['currency_code'] ?? 'EUR'),
      sale: this.normalizePriceString(saleRaw),
      sale_taxed: this.normalizePriceString(saleTaxedRaw),
      list: this.normalizePriceString(listRaw),
      consumer: p['consumer'] == null ? null : String(p['consumer']),
      consumer_uom: p['consumer_uom'] == null ? null : String(p['consumer_uom']),
      consumer_discount: p['consumer_discount'] == null ? null : String(p['consumer_discount']),
      customer: p['customer'] == null ? null : String(p['customer']),
      customer_uom: p['customer_uom'] == null ? null : String(p['customer_uom']),
      price_per_price_uom: Boolean(p['price_per_price_uom'] ?? false),
      price_uom: p['price_uom'] == null ? null : String(p['price_uom']),
      display_as_discount: Boolean(p['display_as_discount'] ?? false),
    }
  }


  private renderStickyProductPreviews() {
    return Array.from({ length: MAX_COMPARE_PRODUCTS }, (_, i) => {
      const item = this.products[i]
      if (!item) {
        return html`<div
          class="sticky-col placeholder"
          aria-hidden="true"
        ></div>`
      }
      const id = Number(item.product.id)
      return html`
        <div
          class=${classMap({
            'sticky-col': true,
            removing: this.removingProductIds.has(id),
          })}
        >
          <csc-product-preview
            product-name="${item.product.name}"
            product-id="${item.product.code}"
            .price=${this.displayPrices ? this.formatComparePriceForPreview(item.product.prices) : ''}
            image-source="${this.previewImageUrl(item.product)}"
            size="small"
          ></csc-product-preview>
        </div>
      `
    })
  }

  private renderStickyStrip() {
    if (!this.stickyStripArmed) return ''

    const stripClasses = {
      'sticky-strip': true,
      visible: this.compareStickyStripVisible && !this.stickyStripDismissing,
      dismissing: this.stickyStripDismissing,
    }

    return html`
      <div
        class=${classMap(stripClasses)}
        aria-hidden="${this.compareStickyStripVisible && !this.stickyStripDismissing ? 'false' : 'true'}"
      >
        <cse-sticky-bar position="top">
          <div class="sticky-inner">
            <div class="gutter" aria-hidden="true"></div>
            <div class="sticky-products">
              ${this.renderStickyProductPreviews()}
            </div>
          </div>
        </cse-sticky-bar>
      </div>
    `
  }

  private orderProductsByRequestedIds(list: ComparePageProduct[]): ComparePageProduct[] {
    const byId = new Map(list.map((p) => [Number(p.product.id), p]))
    return this.productIds
      .map((id) => byId.get(Number(id)))
      .filter((p): p is ComparePageProduct => p != null)
  }

  /** Resolve GET compare endpoint. Themes often pass either PLP-style (url_root + api-full-url) or only url_for(compare) as api-base-url. */
  private resolveCompareApiUrl(): URL {
    const pageHref = typeof window !== 'undefined' ? window.location.href : 'http://localhost/'
    if (this.apiFullUrl.trim() !== '') {
      return new URL(this.apiFullUrl, this.apiBaseUrl.trim() !== '' ? this.apiBaseUrl : pageHref)
    }

    const baseRaw = this.apiBaseUrl.trim() !== '' ? this.apiBaseUrl : window.location.origin
    const candidate = new URL(baseRaw, pageHref)
    const path = candidate.pathname.replace(/\/+$/, '') || '/'
    if (path !== '/' && path !== '') {
      return candidate
    }
    return new URL(`/${this.locale}${COMPARE_API_PATH}`, candidate.origin)
  }

  private async fetchCompareData() {
    if (this.productIds.length === 0) return
    this.fetchAbort?.abort()
    this.fetchAbort = new AbortController()
    const { signal } = this.fetchAbort

    const apiUrlObj = this.resolveCompareApiUrl()
    const params = new URLSearchParams(apiUrlObj.search)
    const ids = this.productIds.slice(0, MAX_COMPARE_PRODUCTS)
    params.delete('id')
    ids.forEach((id) => params.append('id', String(id)))
    const query = params.toString()
    const pathOnly = (() => {
      let p = apiUrlObj.pathname || '/'
      if (p.length > 1) p = p.replace(/\/+$/, '')
      return p || '/'
    })()
    const querySuffix = query ? `?${query}` : ''
    const sameOrigin =
      typeof window !== 'undefined' && apiUrlObj.origin === window.location.origin
    const url = sameOrigin
      ? `${pathOnly}${querySuffix}`
      : `${apiUrlObj.origin}${pathOnly}${querySuffix}`

    const imageOrigin = (() => {
      try { return apiUrlObj.origin }
      catch { return window.location.origin }
    })()

    this.isLoading = true
    this.fetchError = null
    try {
      const res = await fetch(url, { signal, headers: { accept: 'application/json' } })
      if (!res.ok) throw new Error(`Compare API error: ${res.status}`)
      const data = await res.json()
      const rawList: ComparePageProduct[] = Array.isArray(data) ? data : (data?.products ?? [])
      const list = this.orderProductsByRequestedIds(rawList)
      list.forEach((item) => {
        item.product.prices = this.normalizeComparePrices(item.product.prices)
        const ordering = item.product.ordering as unknown as Record<string, unknown>
        if (!ordering['default_uom']) ordering['default_uom'] = 'Unit'
        if (ordering['cannot_be_added_to_basket'] == null) ordering['cannot_be_added_to_basket'] = false
        if (ordering['order_quantity'] == null) ordering['order_quantity'] = 1
      })
      list.forEach((item) => {
        const p = item.product as unknown as Record<string, unknown>
        const picture = p['picture'] as Record<string, string> | undefined
        const defaultPicture = p['default_picture'] as Record<string, string> | undefined
        if (picture) {
          (['mini', 'small', 'normal', 'large'] as const).forEach((size) => {
            if (picture[size] && picture[size].startsWith('/')) picture[size] = `${imageOrigin}${picture[size]}`
          })
        }
        if (defaultPicture) {
          (['mini', 'small', 'normal', 'large'] as const).forEach((size) => {
            if (defaultPicture[size] && defaultPicture[size].startsWith('/')) defaultPicture[size] = `${imageOrigin}${defaultPicture[size]}`
          })
        }
      })
      if (list.length === 0) {
        throw new Error('No products could be loaded from compare endpoint')
      }
      this.products = list
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      this.fetchError = err instanceof Error ? err.message : String(err)
      this.products = []
    } finally {
      this.isLoading = false
    }
  }

  private isGeneralAttributeGroup(group: AttributeGroup): boolean {
    if (group.name === 'grouped') return false
    return stripGroupNamePrefix(group.name) === 'General'
  }

  private renderGroupAccordionTitle(group: AttributeGroup) {
    if (group.name === 'grouped') {
      return t('group_specifications', { ns: 'csc-compare-page', default: 'Specifications' })
    }
    if (this.isGeneralAttributeGroup(group)) {
      return t('group_general', { ns: 'csc-compare-page', default: 'General' })
    }
    return group.displayName
  }

  private get attributeGroups(): AttributeGroup[] {
    if (this.products.length === 0) return []

    const propsInBundled = new Set<string>()
    const bundledGroupNames = new Set<string>()
    const groupToProps = new Map<string, Set<string>>()

    for (const item of this.products) {
      for (const entry of item.bundled_properties ?? []) {
        const propName = entry.property_name
        if (!propName) continue
        const groupName = entry.group_name ?? '0-None'
        bundledGroupNames.add(groupName)
        propsInBundled.add(propName)
        if (!groupToProps.has(groupName)) groupToProps.set(groupName, new Set())
        const propSet = groupToProps.get(groupName)
        if (propSet) propSet.add(propName)
      }
    }

    const groupedOnly = new Set<string>()
    for (const item of this.products) {
      for (const propName of Object.keys(item.grouped_properties ?? {})) {
        if (!propsInBundled.has(propName)) groupedOnly.add(propName)
      }
    }

    const first = this.products[0]
    if (!first) return []

    const groups: AttributeGroup[] = []

    if (groupedOnly.size > 0) {
      const order = Object.keys(first.grouped_properties ?? {})
      const ordered = [...groupedOnly].sort((a, b) => {
        const ia = order.indexOf(a)
        const ib = order.indexOf(b)
        return ia >= 0 && ib >= 0 ? ia - ib : ia >= 0 ? -1 : ib >= 0 ? 1 : 0
      })
      groups.push({ name: 'grouped', displayName: 'Specifications', properties: ordered })
    }

    const firstBundled = first.bundled_properties ?? []
    const bundledOrder: string[] = []
    const seenGroups = new Set<string>()
    for (const entry of firstBundled) {
      const gn = entry.group_name ?? '0-None'
      if (!seenGroups.has(gn)) {
        seenGroups.add(gn)
        bundledOrder.push(gn)
      }
    }

    const propOrderInFirstGroup = (groupName: string): string[] => {
      const order: string[] = []
      const seen = new Set<string>()
      for (const entry of firstBundled) {
        if ((entry.group_name ?? '0-None') !== groupName) continue
        const pn = entry.property_name
        if (!pn || seen.has(pn)) continue
        seen.add(pn)
        order.push(pn)
      }
      return order
    }

    for (const groupName of bundledOrder) {
      const props = groupToProps.get(groupName)
      if (props && props.size > 0) {
        const propOrder = propOrderInFirstGroup(groupName)
        const sortByOrder = (a: string, b: string) => {
          const ia = propOrder.indexOf(a)
          const ib = propOrder.indexOf(b)
          return ia >= 0 && ib >= 0 ? ia - ib : ia >= 0 ? -1 : ib >= 0 ? 1 : 0
        }
        const ordered = [...props].sort(sortByOrder)
        groups.push({ name: groupName, displayName: stripGroupNamePrefix(groupName), properties: ordered })
      }
    }

    for (const groupName of bundledGroupNames) {
      if (bundledOrder.includes(groupName)) continue
      const props = groupToProps.get(groupName)
      if (props && props.size > 0) {
        groups.push({ name: groupName, displayName: stripGroupNamePrefix(groupName), properties: [...props] })
      }
    }

    return groups
  }


  private get filteredAttributeGroups(): AttributeGroup[] {
    let groups = this.attributeGroups

    if (this.hiddenProperties.length > 0) {
      const normalize = (s: string) => s.trim().toLowerCase()
      const hidden = new Set(this.hiddenProperties.map(normalize))
      groups = groups.map((group) => ({
        ...group,
        properties: group.properties.filter((propName) => !hidden.has(normalize(propName))),
      }))
    }

    if (!this.onlyShowDifferences || this.products.length < 2) return groups
    return groups.map((group) => ({
      ...group,
      properties: group.properties.filter((propName) => this.propertyHasDifferentValues(propName)),
    }))
  }

  private propertyHasDifferentValues(propName: string): boolean {
    const values = this.products.map((p) => this.getPropertyValue(p, propName))
    const unique = new Set(values)
    return unique.size > 1
  }

  /**
   * Rewrites the browser URL query so every `id` matches `this.productIds` in order.
   * Other query keys are preserved. Uses replaceState (no extra history entries).
   */
  private syncCompareUrlFromProductIds(): void {
    if (typeof window === 'undefined') return
    try {
      const url = new URL(window.location.href)
      const next = new URLSearchParams()
      const current = new URLSearchParams(url.search)
      for (const [key, value] of current) {
        if (key !== 'id') {
          next.append(key, value)
        }
      }
      for (const pid of this.productIds) {
        next.append('id', String(pid))
      }
      const search = next.toString()
      const href = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
      window.history.replaceState(window.history.state, '', href)
    } catch {
      /* ignore invalid location */
    }
  }

  private removeProduct(productId: number | string) {
    const id = Number(productId)
    if (this.removingProductIds.has(id)) return
    const isLast = this.productIds.length === 1 && Number(this.productIds[0]) === id

    this.removingProductIds = new Set([...this.removingProductIds, id])
    if (!isLast) {
      window.dispatchEvent(new CscCompareRemoveEvent(id))
    }

    setTimeout(() => {
      this.products = this.products.filter((p) => Number(p.product.id) !== id)
      this.productIds = this.productIds.filter((pid) => Number(pid) !== id)
      this.syncCompareUrlFromProductIds()
      if (this.productIds.length === 0) {
        clearAllCompareSelections()
        window.dispatchEvent(new CscCompareClearEvent())
      } else {
        removeProductIdFromAllCompareSelections(id)
      }
      this.removingProductIds.delete(id)
      this.removingProductIds = new Set(this.removingProductIds)
    }, COMPARE_ANIM_MS)
  }

  private handleOnlyDifferencesChange(e: Event) {
    const target = e.target as { checked?: boolean }
    if (target && typeof target.checked === 'boolean') {
      this.onlyShowDifferences = target.checked
    }
  }


  private getPropertyValue(product: ComparePageProduct, propName: string): string {
    const entry = (product.bundled_properties ?? []).find((e) => e.property_name === propName)
    if (entry) {
      const main = formatBundledValues(entry.value)
      if (main !== '-') {
        const uom = entry.uom != null && String(entry.uom).trim() !== '' ? ` ${entry.uom}` : ''
        return main + uom
      }
    }

    const grouped = product.grouped_properties ?? {}
    if (propName in grouped) {
      const v = grouped[propName]
      if (v != null && String(v).trim() !== '') return String(v)
    }

    return '-'
  }

  private renderColgroup() {
    return html`
      <colgroup>
        <col class="col-label" />
        ${Array.from({ length: MAX_COMPARE_PRODUCTS }, () => html`<col class="col-product" />`)}
      </colgroup>
    `
  }



  private renderLoadingAttributeRow() {
    return html`
      <tr class="row">
        <td class="cell label">
          <span class="label-inner">
            <cse-skeleton
              variant="secondary"
              height="18"
              style="display: block; width: 120px; max-width: 100%;"
            ></cse-skeleton>
          </span>
        </td>
        ${Array.from({ length: MAX_COMPARE_PRODUCTS }, () => html`
          <td class="cell value">
            <cse-skeleton
              variant="secondary"
              height="18"
              style="display: block; width: 100%;"
            ></cse-skeleton>
          </td>
        `)}
      </tr>
    `
  }

  /** Skeleton column count while loading (matches requested ids, capped at max compare slots). */
  private loadingTileColumnCount(): number {
    const n = this.productIds.length
    if (n <= 0) return MAX_COMPARE_PRODUCTS
    return Math.min(n, MAX_COMPARE_PRODUCTS)
  }

  private renderLoadingTilesRow() {
    const cols = this.loadingTileColumnCount()
    return html`
      <div class="tiles">
        <div class="gutter" aria-hidden="true"></div>
        <div class="tile-list">
          ${Array.from({ length: cols }, () => html`
            <div class="tile">
              <cse-skeleton variant="secondary" height="400"></cse-skeleton>
            </div>
          `)}
        </div>
      </div>
    `
  }

  private renderLoadingAccordionSkeleton() {
    const skeletonRowsAmount = 5
    const groups = [
      { title: t('group_specifications', { ns: 'csc-compare-page', default: 'Specifications' }) },
      { title: 'Details' },
    ]

    return groups.map(
      (group) => html`
        <cse-accordion variant="simple" open>
          <cse-text slot="title" as="h3" variant="heading-4">${group.title}</cse-text>
          <div slot="content" class="accordion-content">
            <table class="table">
              ${this.renderColgroup()}
              <tbody>
                ${Array.from({ length: skeletonRowsAmount }, () =>
                  this.renderLoadingAttributeRow())}
              </tbody>
            </table>
          </div>
        </cse-accordion>
      `,
    )
  }

  private renderTilesRow() {
    return html`
      <div class="tiles">
        <div class="gutter" aria-hidden="true"></div>
        <div class="tile-list">
          ${this.tileProducts.map((tile) => this.renderCompareTile(tile))}
        </div>
      </div>
    `
  }

  /**
   * csc-price's locale must match `[a-z]{2}-[A-Z]{2}` (e.g. nl-NL).
   * Themes sometimes pass `nl_NL` or short codes — fall back to en-US for invalid values.
   */
  private tileInjectorLocale(): string {
    const raw = String(this.locale ?? '').replace('_', '-').trim()
    if (/^[a-z]{2}-[A-Z]{2}$/.test(raw)) return raw
    return 'en-US'
  }

  private renderCompareTile(tile: { id: number; product: ComparePageProductTile }) {
    const product = tile.product
    const cannotOrder = !!product.ordering.cannot_be_added_to_basket || product.status.can_order_product === false

    return html`
      <div
        class=${classMap({
          tile: true,
          removing: this.removingProductIds.has(tile.id),
        })}
      >
        <csc-product-image
          source="${product.picture.small}"
          fallback-source="${product.default_picture.small}"
          alt="${product.name}"
          url="${product.page_link}"
        ></csc-product-image>
        <cse-group class="tile-group" direction="column" row-gap="05">
          <cse-link url="${product.page_link}" variant="secondary" font-variant="product-title-m">
            ${product.name}
          </cse-link>
          <cse-text variant="body-s">
            ${t('product_code', { ns: 'csc-product-tile', default: 'code: ' })}${product.code}
          </cse-text>
          ${this.displayPrices ? html`
            <csc-price
              sale-price="${parsePrice(product.prices.sale)}"
              sale-price-taxed="${parsePrice(product.prices.sale_taxed)}"
              consumer-price="${parsePrice(product.prices.consumer)}"
              currency-code="${product.prices.currency_code}"
              locale="${this.tileInjectorLocale()}"
              use-consumer-price
            ></csc-price>
          ` : nothing}
        </cse-group>
        <csc-order-form
          product-id="${product.id}"
          min="${ifDefined(product.ordering.minimum_order_quantity ?? undefined)}"
          max="${ifDefined(product.ordering.maximum_order_quantity ?? undefined)}"
          step="${product.ordering.order_quantity ?? 1}"
          default="${product.ordering.default_order_quantity ?? product.ordering.order_quantity ?? 1}"
          ?cannot-be-added-to-basket=${cannotOrder}
        ></csc-order-form>
        <cse-icon-button
          class="tile-close"
          variant="primary"
          size="default"
          aria-label="Remove product"
          @click=${() => this.removeProduct(tile.id)}
        >
          <cse-icon icon-name="xmark" icon-style="light"></cse-icon>
        </cse-icon-button>
      </div>
    `
  }

  private renderAttributeRow(propName: string) {
    const placeholderCount = MAX_COMPARE_PRODUCTS - this.products.length
    return html`
      <tr class="row">
        <td class="cell label">
          <span class="label-inner">
            <cse-text variant="body-s">${propName}</cse-text>
          </span>
        </td>
        ${this.products.map((item) => {
          const value = this.getPropertyValue(item, propName)
          const isCheckmark = value === '✓' || value === 'true' || value === 'yes'

          return html`
            <td class="cell value">
              ${isCheckmark
                ? html`<cse-icon class="check" icon-name="check" icon-style="solid"></cse-icon>`
                : html`<cse-text variant="body">${value}</cse-text>`}
            </td>
          `
        })}
        ${Array.from({ length: placeholderCount }, () => html`
          <td
            class="cell value placeholder"
            aria-hidden="true"
          ></td>
        `)}
      </tr>
    `
  }

  override render() {
    const showLoadingUi =
      !this.fetchError &&
      (this.isLoading || (this.productIds.length > 0 && this.products.length === 0))

    if (showLoadingUi) {
      const cols = this.loadingTileColumnCount()
      return html`
        <div
          class="page"
          style="--csc-compare-product-count: ${MAX_COMPARE_PRODUCTS}; --csc-compare-active-product-count: ${cols}"
        >
          <div class="scroll-region">
            <div class="scroll-inner">
              <div class="header">
                ${this.renderLoadingTilesRow()}
              </div>
              <cse-accordion-group class="accordion-group" multiopen>
                ${this.renderLoadingAccordionSkeleton()}
              </cse-accordion-group>
            </div>
          </div>
        </div>
      `
    }
    if (this.fetchError) {
      return html`
        <div class="page">
          <cse-text variant="body">Oops, something went wrong while loading the products. Please try again later.</cse-text>
        </div>
      `
    }
    /* Empty: no ids to compare (user cleared all, or opened compare without selection / URL ids). */
    if (!this.fetchError && this.products.length === 0 && this.productIds.length === 0) {
      return html`
        <div class="page empty">
          <cse-text class="empty-description" variant="body">
            ${t('empty_description', {
              ns: 'csc-compare-page',
              default:
                'Please select at least 2 products to compare. You can add a product to the comparison by clicking compare for that product. The product will appear in the comparison. Up to 4 products.',
            })}
          </cse-text>
        </div>
      `
    }

    return html`
        <div
        class="page"
        style="--csc-compare-product-count: 4; --csc-compare-active-product-count: ${this.products.length}"
      >
        <div class="sticky-sentinel" aria-hidden="true"></div>
        ${this.products.length > 0 ? this.renderStickyStrip() : ''}
        <div
          class="scroll-region"
        >
          <div class="scroll-inner">
            <div class="header" data-cs-compare-products-row>
              ${this.renderTilesRow()}
            </div>
            <div class="toggle-wrap">
              <cse-toggle
                .checked=${this.onlyShowDifferences}
                @change=${this.handleOnlyDifferencesChange}
              >${t('only_differences', { ns: 'csc-compare-page', default: 'Only show differences' })}</cse-toggle>
            </div>
            <cse-accordion-group class="accordion-group" multiopen>
              ${this.filteredAttributeGroups
                .filter((group) => group.properties.length > 0)
                .map(
                  (group) => html`
                    <cse-accordion variant="simple" open>
                      <cse-text slot="title" as="h3" variant="heading-4">${this.renderGroupAccordionTitle(group)}</cse-text>
                      <div slot="content" class="accordion-content">
                        <table class="table">
                          ${this.renderColgroup()}
                          <tbody>
                            ${group.properties.map((prop) => this.renderAttributeRow(prop))}
                          </tbody>
                        </table>
                      </div>
                    </cse-accordion>
                  `,
                )}
            </cse-accordion-group>
            <div
              class="table-end-sentinel"
              data-cs-compare-table-end
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-compare-page': CSCComparePage
  }
}
